import type {
  ChangeTenantUserEmailRequest,
  ActivateUserRequest,
  TenantUser,
  DeactivateUserRequest,
  ScheduleUserDeactivationRequest,
  ChangeTenantUserTitleRequest,
  ChangeTenantUserNameRequest,
  ChangeTenantUserPhoneNumberRequest,
  ChangeTenantUserProfileImageRequest,
  GrantTenantAdminPrivilegesRequest,
  RevokeTenantAdminPrivilegesRequest
} from '@gat-ship/types'
import { inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  appLoadingKey,
  tenantUserDetailKey,
  tenantUsersCollectionKey,
  type AppLoadingContext,
  type TenantUserDetailService,
  type TenantUsersCollectionService
} from '@gat-ship/types/injection-keys'
import { useTenantUserSignalREvents } from '@gat-ship/signalr'
import { useTenantUserApiCalls } from './useTenantUserApiCalls'
import { globalUtils } from '@gat-ship/utils'

export const tenantUserLoadingIds = {
  tenantUser: (id: string) => `tenant-user-${id}`,
  activateUser: 'tenant-user-activate',
  deactivateUser: 'tenant-user-deactivate',
  scheduleDeactivation: 'tenant-user-schedule-deactivation',
  changeEmail: 'tenant-user-change-email',
  changeJobTitle: 'tenant-user-change-job-title',
  changeName: 'tenant-user-change-name',
  changePhoneNumber: 'tenant-user-change-phone-number',
  changeProfileImage: 'tenant-user-change-profile-image',
  grantTenantAdminPrivileges: 'tenant-user-grant-tenant-admin-privileges',
  revokeTenantAdminPrivileges: 'tenant-user-revoke-tenant-admin-privileges'
}

function defineTenantUserState(
  loading: AppLoadingContext,
  props?: { tenantUserId?: string }
) {
  const provider = ref<TenantUsersCollectionService | undefined>(undefined)
  onMounted(() => {
    try {
      provider.value = inject(tenantUsersCollectionKey, undefined)
    } catch {
      // Do nothing.
    }
  })

  const { getTenantUserRequest } = useTenantUserApiCalls()

  const { setLoadingId, removeLoadingId } = loading

  const tenantUser = ref<TenantUser | undefined>(undefined)

  function _setTenantUser(data: TenantUser) {
    tenantUser.value = data
  }

  /**
   * Fetches the tenant user.
   * @param userId - The ID of the tenant user to fetch.
   * @param options - Optional options for the fetch.
   * @param options.forceFetch - Whether to force a fetch of the tenant user. @default false
   */
  async function fetchUser(options?: { forceFetch?: boolean }) {
    const userId = props?.tenantUserId
    if (!userId) {
      return
    }

    setLoadingId(tenantUserLoadingIds.tenantUser(userId))

    try {
      if (provider.value) {
        if (options?.forceFetch) {
          await provider.value.fetchTenantUsers({ forceFetch: true })
        }
        const found = provider.value.tenantUsers.find(
          (user: TenantUser) => user.userId === userId
        )
        if (found) {
          _setTenantUser(found)
          return
        }
      }
      if (userId !== tenantUser.value?.userId || options?.forceFetch) {
        const result = await getTenantUserRequest(userId)
        _setTenantUser(result.user)
      }
    } finally {
      signalREvents.subscribeToTenantUserEvents()
      removeLoadingId(tenantUserLoadingIds.tenantUser(userId))
    }
  }

  watch(
    () => props?.tenantUserId,
    (newVal) => {
      if (newVal) {
        fetchUser({ forceFetch: true })
      }
    },
    { immediate: true }
  )

  const signalREvents = useTenantUserSignalREvents({
    onTenantUserNameChanged: async (payload) => {
      if (tenantUser.value?.userId === payload.userId) {
        fetchUser({ forceFetch: true })
      }
    },
    onTenantUserPhoneNumberChanged: async (payload) => {
      if (tenantUser.value?.userId === payload.userId) {
        fetchUser({ forceFetch: true })
      }
    },
    onTenantUserDeactivated: async (payload) => {
      if (tenantUser.value?.userId === payload.userId) {
        fetchUser({ forceFetch: true })
      }
    },
    onTenantUserActivated: async (payload) => {
      if (tenantUser.value?.userId === payload.userId) {
        fetchUser({ forceFetch: true })
      }
    },
    onTenantAdminGranted: async (payload) => {
      if (tenantUser.value?.userId === payload.message.userId) {
        fetchUser({ forceFetch: true })
      }
    },
    onTenantAdminRevoked: async (payload) => {
      if (tenantUser.value?.userId === payload.message.userId) {
        fetchUser({ forceFetch: true })
      }
    },
    onTenantUserEmailChanged: async (payload) => {
      if (tenantUser.value?.userId === payload.message.userId) {
        fetchUser({ forceFetch: true })
      }
    },
    onTenantUserProfileImageChanged: async (payload) => {
      if (tenantUser.value?.userId === payload.message.userId) {
        fetchUser({ forceFetch: true })
      }
    },
    onUserDeactivationScheduled: async (payload) => {
      if (tenantUser.value?.userId === payload.userId) {
        fetchUser({ forceFetch: true })
      }
    },
    onUserActivated: async (payload) => {
      if (tenantUser.value?.userId === payload.userId) {
        fetchUser({ forceFetch: true })
      }
    },
    onUserJobTitleChanged: async (payload) => {
      if (tenantUser.value?.userId === payload.message.userId) {
        fetchUser({ forceFetch: true })
      }
    }
  })

  onBeforeUnmount(() => {
    signalREvents.unsubscribeFromTenantUserEvents()
  })

  return {
    tenantUser,
    fetchUser
  }
}

function defineTenantUserRequests(
  loading: AppLoadingContext,
  tenantUserState: ReturnType<typeof defineTenantUserState>
) {
  const {
    changeTenantUserEmailRequest,
    activateUserRequest,
    deactivateUserRequest,
    scheduleUserDeactivationRequest,
    changeTenantUserTitleRequest,
    changeNameOfUserRequest,
    changeTenantUserPhoneNumberRequest,
    changeTenantUserProfileImageRequest,
    grantTenantAdminPrivilegesRequest,
    revokeTenantAdminPrivilegesRequest
  } = useTenantUserApiCalls()

  const { setLoadingId, removeLoadingId } = loading

  async function activateUser(payload: ActivateUserRequest) {
    const loadingId = tenantUserLoadingIds.activateUser
    try {
      setLoadingId(loadingId)
      await activateUserRequest(payload, globalUtils.functions.uuid())
      await tenantUserState.fetchUser({ forceFetch: true })
    } finally {
      removeLoadingId(loadingId)
    }
  }

  async function deactivateUser(payload: DeactivateUserRequest) {
    const loadingId = tenantUserLoadingIds.deactivateUser
    try {
      setLoadingId(loadingId)
      await deactivateUserRequest(payload, globalUtils.functions.uuid())
      await tenantUserState.fetchUser({ forceFetch: true })
    } finally {
      removeLoadingId(loadingId)
    }
  }

  async function removeDeactivationSchedule(
    payload: ScheduleUserDeactivationRequest
  ) {
    const loadingId = tenantUserLoadingIds.scheduleDeactivation
    try {
      setLoadingId(loadingId)
      await scheduleUserDeactivationRequest(
        payload,
        globalUtils.functions.uuid()
      )
      await tenantUserState.fetchUser({ forceFetch: true })
    } finally {
      removeLoadingId(loadingId)
    }
  }

  async function changeEmail(payload: ChangeTenantUserEmailRequest) {
    const loadingId = tenantUserLoadingIds.changeEmail
    try {
      setLoadingId(loadingId)
      await changeTenantUserEmailRequest(payload, globalUtils.functions.uuid())
      await tenantUserState.fetchUser({ forceFetch: true })
    } finally {
      removeLoadingId(loadingId)
    }
  }

  async function changeJobTitle(payload: ChangeTenantUserTitleRequest) {
    const loadingId = tenantUserLoadingIds.changeJobTitle
    try {
      setLoadingId(loadingId)
      await changeTenantUserTitleRequest(payload, globalUtils.functions.uuid())
      await tenantUserState.fetchUser({ forceFetch: true })
    } finally {
      removeLoadingId(loadingId)
    }
  }

  async function changeName(payload: ChangeTenantUserNameRequest) {
    const loadingId = tenantUserLoadingIds.changeName
    try {
      setLoadingId(loadingId)
      await changeNameOfUserRequest(payload, globalUtils.functions.uuid())
      await tenantUserState.fetchUser({ forceFetch: true })
    } finally {
      removeLoadingId(loadingId)
    }
  }

  async function changePhoneNumber(
    payload: ChangeTenantUserPhoneNumberRequest
  ) {
    const loadingId = tenantUserLoadingIds.changePhoneNumber
    try {
      setLoadingId(loadingId)
      await changeTenantUserPhoneNumberRequest(
        payload,
        globalUtils.functions.uuid()
      )
      await tenantUserState.fetchUser({ forceFetch: true })
    } finally {
      removeLoadingId(loadingId)
    }
  }

  async function changeProfileImage(
    payload: ChangeTenantUserProfileImageRequest
  ) {
    const loadingId = tenantUserLoadingIds.changeProfileImage
    try {
      setLoadingId(loadingId)
      await changeTenantUserProfileImageRequest(
        payload,
        globalUtils.functions.uuid()
      )
      await tenantUserState.fetchUser({ forceFetch: true })
    } finally {
      removeLoadingId(loadingId)
    }
  }

  async function scheduleDeactivation(
    payload: ScheduleUserDeactivationRequest
  ) {
    const loadingId = tenantUserLoadingIds.scheduleDeactivation
    try {
      setLoadingId(loadingId)
      await scheduleUserDeactivationRequest(
        payload,
        globalUtils.functions.uuid()
      )
      await tenantUserState.fetchUser({ forceFetch: true })
    } finally {
      removeLoadingId(loadingId)
    }
  }

  async function grantTenantAdminPrivileges(
    payload: GrantTenantAdminPrivilegesRequest
  ) {
    const loadingId = tenantUserLoadingIds.grantTenantAdminPrivileges
    try {
      setLoadingId(loadingId)
      await grantTenantAdminPrivilegesRequest(
        payload,
        globalUtils.functions.uuid()
      )
      await tenantUserState.fetchUser({ forceFetch: true })
    } finally {
      removeLoadingId(loadingId)
    }
  }

  async function revokeTenantAdminPrivileges(
    payload: RevokeTenantAdminPrivilegesRequest
  ) {
    const loadingId = tenantUserLoadingIds.revokeTenantAdminPrivileges
    try {
      setLoadingId(loadingId)
      await revokeTenantAdminPrivilegesRequest(
        payload,
        globalUtils.functions.uuid()
      )
      await tenantUserState.fetchUser({ forceFetch: true })
    } finally {
      removeLoadingId(loadingId)
    }
  }

  return {
    activateUser,
    deactivateUser,
    changeEmail,
    removeDeactivationSchedule,
    changeJobTitle,
    changeName,
    changePhoneNumber,
    changeProfileImage,
    scheduleDeactivation,
    grantTenantAdminPrivileges,
    revokeTenantAdminPrivileges
  }
}

/**
 * Detail datasource for a single tenant user.
 *
 * The loading handle is the app-wide loading registry, provided once at the
 * root by `LoadingProvider` and consumed here via its injection key
 * (`appLoadingKey`, keyed in `@gat-ship/types`) rather than threaded in as a
 * parameter — keeping the datasource UI-free. The sibling collection is
 * resolved via `inject(tenantUsersCollectionKey)` (swallow-on-missing
 * preserved).
 */
export function useTenantUserDetail(props?: {
  tenantUserId?: string
}): TenantUserDetailService {
  const loading = inject(appLoadingKey)!

  const tenantUserState = defineTenantUserState(loading, props)

  const commands = defineTenantUserRequests(loading, tenantUserState)

  return {
    tenantUser: tenantUserState.tenantUser,
    commands
  }
}

/** Inject the tenant-user detail context provided by a parent provider. */
export function useTenantUserDetailContext() {
  return inject(tenantUserDetailKey, undefined)
}
